import React from 'react';
import { Check, Loader2, Sparkles } from 'lucide-react';

export type BulkExtractionProgressOverlayProps = {
  open: boolean;
  title: string;
  subtitle: string;
  statusMessage: string;
  detailMessage?: string | null;
  /** When true, shows success check instead of spinner (matches control extraction flow). */
  isComplete: boolean;
};

/**
 * Full-screen progress card used after bulk document upload (control extraction, process bulk upload, etc.).
 */
export function BulkExtractionProgressOverlay({
  open,
  title,
  subtitle,
  statusMessage,
  detailMessage,
  isComplete,
}: BulkExtractionProgressOverlayProps): React.ReactElement | null {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
      <div className="w-full max-w-md rounded-2xl border border-gray-100 bg-white p-10 shadow-xl">
        <div className="flex flex-col items-center space-y-8">
          <div className="relative">
            {!isComplete ? (
              <div className="absolute inset-0 animate-ping rounded-full bg-blue-400 opacity-20" />
            ) : null}
            <div
              className={`relative flex h-20 w-20 items-center justify-center rounded-full transition-all duration-500 ${
                isComplete
                  ? 'scale-110 bg-gradient-to-br from-green-400 to-green-600'
                  : 'bg-gradient-to-br from-blue-500 to-purple-600'
              }`}
            >
              {!isComplete ? <Loader2 className="h-10 w-10 animate-spin text-white" /> : null}
              {isComplete ? <Check className="h-10 w-10 text-white" /> : null}
            </div>
            <div className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-blue-600 to-purple-600 shadow-lg">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
          </div>

          <div className="space-y-3 text-center">
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
            <p className="text-sm leading-relaxed text-gray-600">{subtitle}</p>
          </div>

          <div className="w-full rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-purple-50 p-4">
            <p className="text-center text-sm font-medium text-blue-900">{statusMessage}</p>
            {detailMessage ? (
              <p className="mt-2 text-center text-xs text-blue-700 opacity-75">{detailMessage}</p>
            ) : null}
          </div>

          {!isComplete ? (
            <div className="flex gap-2">
              <div className="h-2 w-2 animate-bounce rounded-full bg-blue-400" />
              <div
                className="h-2 w-2 animate-bounce rounded-full bg-purple-400"
                style={{ animationDelay: '0.2s' }}
              />
              <div
                className="h-2 w-2 animate-bounce rounded-full bg-blue-400"
                style={{ animationDelay: '0.4s' }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default BulkExtractionProgressOverlay;
